import type { BlockDefinition, CarouselProps, CarouselSlide } from '../types';
import { ensureHttps, escapeHtml, maybeTokenize, safeUrl } from '../html';

const ASPECT_VALUES: CarouselProps['aspectRatio'][] = ['auto', '1/1', '3/4', '9/16', '4/3', '16/9'];

/**
 * Image carousel — swipeable horizontal gallery with a "peek" affordance
 * instead of dot indicators. Each slide takes ~84% of the container width,
 * so the next/previous image always peeks in from the edge — that's the
 * visual cue telling the visitor "there's more, swipe to see it."
 *
 * 100% CSS for the swipe: `overflow-x: auto` + `scroll-snap-type: x mandatory`
 * gives native touch swipe + trackpad swipe + (on desktop) horizontal mouse
 * wheel. No JavaScript needed for the core interaction.
 *
 * A subtle mask-image gradient fades the edges to make the peek effect
 * feel polished rather than abruptly clipped. The scrollbar is hidden
 * (it'd clutter the carousel and most touch devices don't render it
 * anyway).
 *
 * Optional `showCounter` overlays a "1 / 5" pill in the top-right of each
 * slide — useful when there are many images and visitors might wonder
 * how deep the gallery goes. Tracked client-side with a ~400-byte JS
 * snippet that watches scroll-snap position; off by default to keep the
 * carousel zero-JS by default.
 */
export const carousel: BlockDefinition<CarouselProps> = {
  type: 'carousel',
  meta: {
    label: 'Carousel',
    icon: '🖼️',
    category: 'media',
    description: 'Swipeable image gallery with peek-the-next-image affordance.',
  },
  defaults: () => ({
    slides: [
      { src: '', alt: '', caption: '', url: '' },
      { src: '', alt: '', caption: '', url: '' },
    ],
    aspectRatio: '1/1',
    shape: 'rounded',
    showCounter: false,
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<CarouselProps>;
    const raw = Array.isArray(i.slides) ? i.slides : [];
    return {
      slides: raw
        .map(validateSlide)
        .filter((s): s is CarouselSlide => s !== null)
        .slice(0, 20),
      aspectRatio: ASPECT_VALUES.includes(i.aspectRatio as CarouselProps['aspectRatio'])
        ? (i.aspectRatio as CarouselProps['aspectRatio'])
        : '1/1',
      shape: i.shape === 'square' ? 'square' : 'rounded',
      showCounter: !!i.showCounter,
    };
  },
  render(props, _theme, ctx) {
    if (props.slides.length === 0) {
      // Empty state matches the image block's empty placeholder.
      return `<div class="pb-block pb-carousel" style="aspect-ratio:1/1;background:rgba(0,0,0,0.06);border:2px dashed rgba(0,0,0,0.15);border-radius:${
        props.shape === 'square' ? '0' : 'var(--radius-card)'
      };display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:var(--text);opacity:0.55;font-size:14px;">
  <span style="font-size:28px;">🖼️</span>
  <span style="font-weight:600;">Add images to your carousel</span>
  <span style="font-size:11px;opacity:0.7;">Visitors can swipe between them — drag or tap to add slides on the right.</span>
</div>`;
    }

    const safeId = 'pb_car_' + Math.random().toString(36).slice(2, 10);
    const radius = props.shape === 'square' ? '0' : 'var(--radius-card)';
    const aspect = props.aspectRatio === 'auto' ? '1/1' : props.aspectRatio;
    const total = props.slides.length;

    const slidesHtml = props.slides
      .map((slide, idx) => {
        const counter = props.showCounter
          ? `<span data-pb-counter style="position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.55);color:#fff;font-size:11px;font-weight:600;padding:3px 8px;border-radius:9999px;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);">${idx + 1} / ${total}</span>`
          : '';
        const caption = slide.caption
          ? `<div style="position:absolute;bottom:0;left:0;right:0;padding:18px 14px 12px;background:linear-gradient(to top, rgba(0,0,0,0.55), transparent);color:#fff;font-size:13px;font-weight:500;border-bottom-left-radius:${radius};border-bottom-right-radius:${radius};">${escapeHtml(slide.caption)}</div>`
          : '';
        const inner = `<div style="position:relative;width:100%;aspect-ratio:${aspect};border-radius:${radius};overflow:hidden;background:rgba(0,0,0,0.04);">
  ${slide.src ? `<img src="${safeUrl(slide.src)}" alt="${escapeHtml(slide.alt)}" style="width:100%;height:100%;object-fit:cover;display:block;" loading="lazy" decoding="async" />` : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--text);opacity:0.4;font-size:13px;">Slide ${idx + 1}</div>`}
  ${counter}
  ${caption}
</div>`;
        const wrapped = slide.url
          ? `<a href="${maybeTokenize(safeUrl(ensureHttps(slide.url)), ctx?.tokenizeUrl)}" target="_blank" rel="noopener noreferrer" style="display:block;text-decoration:none;color:inherit;">${inner}</a>`
          : inner;
        return `<div class="pb-car-slide" data-pb-idx="${idx}" style="flex:0 0 84%;scroll-snap-align:center;scroll-snap-stop:always;">${wrapped}</div>`;
      })
      .join('');

    // Counter sync script — only emitted when showCounter is on, since
    // otherwise the carousel is fully zero-JS.
    const counterScript = props.showCounter
      ? `<script>(function(){var root=document.querySelector('[data-pb-id="${safeId}"]');if(!root)return;var track=root.querySelector('[data-pb-track]');var counters=root.querySelectorAll('[data-pb-counter]');if(!track||!counters.length)return;function sync(){var slides=root.querySelectorAll('[data-pb-idx]');var center=track.scrollLeft+track.clientWidth/2;var best=0,bestD=Infinity;slides.forEach(function(s,i){var r=s.offsetLeft+s.offsetWidth/2;var d=Math.abs(r-center);if(d<bestD){bestD=d;best=i;}});counters.forEach(function(c,i){c.textContent=(best+1)+' / ${total}';c.style.opacity=i===best?'1':'0';});}track.addEventListener('scroll',sync,{passive:true});sync();})();</script>`
      : '';

    return `<div class="pb-block pb-carousel" data-pb-id="${safeId}">
  <div data-pb-track style="display:flex;gap:10px;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;-webkit-overflow-scrolling:touch;padding:4px 8% 4px 8%;scrollbar-width:none;mask-image:linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%);-webkit-mask-image:linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%);">
    ${slidesHtml}
  </div>
</div>
<style>.pb-carousel [data-pb-track]::-webkit-scrollbar{display:none}</style>
${counterScript}`;
  },
};

function validateSlide(input: unknown): CarouselSlide | null {
  if (!input || typeof input !== 'object') return null;
  const i = input as Partial<CarouselSlide>;
  return {
    src: typeof i.src === 'string' ? i.src.slice(0, 2000) : '',
    alt: typeof i.alt === 'string' ? i.alt.slice(0, 200) : '',
    caption: typeof i.caption === 'string' ? i.caption.slice(0, 200) : '',
    url: typeof i.url === 'string' ? i.url.slice(0, 2000) : '',
  };
}
